import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
  Plus,
  Loader2,
  Scissors,
  Ban,
} from 'lucide-react';

import {
  useWizardClusters,
  useUnassignedFaces,
  useRemoveFaces,
  useAssignFaces,
  useSplitPerson,
  useDismissCluster,
} from '@/api/hooks/useFaces';
import type {
  FaceDetectionCompact,
  PersonCluster,
} from '@/api/types/face.types';

// ---------------------------------------------------------------------------
// Face thumbnail with selectable state
// ---------------------------------------------------------------------------

function SelectableFace({
  face,
  isSelected,
  isSplitSeed,
  isMoving,
  onToggle,
  onSplitSelect,
  mode,
}: {
  face: FaceDetectionCompact;
  isSelected: boolean;
  isSplitSeed: boolean;
  isMoving: boolean;
  onToggle: () => void;
  onSplitSelect: () => void;
  mode: 'remove' | 'split';
}) {
  if (!face.crop_url) {
    return (
      <div className="flex h-14 w-14 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-700">
        <User className="h-5 w-5 text-gray-400" />
      </div>
    );
  }

  const handleClick = mode === 'split' ? onSplitSelect : onToggle;

  return (
    <button
      onClick={handleClick}
      className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md transition-all duration-500 ${
        isMoving
          ? 'scale-0 opacity-0'
          : isSplitSeed
            ? 'scale-105 ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-900'
            : isSelected
              ? 'ring-2 ring-red-500 ring-offset-1 dark:ring-offset-gray-900'
              : 'ring-1 ring-gray-200 hover:ring-primary-400 dark:ring-gray-700'
      }`}
    >
      <img
        src={face.crop_url}
        alt="Face"
        className={`h-full w-full object-cover transition-opacity duration-300 ${
          isSelected || isSplitSeed ? 'opacity-70' : ''
        }`}
        loading="lazy"
      />
      {isSelected && !isSplitSeed && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-500/30">
          <X className="h-5 w-5 text-white drop-shadow" />
        </div>
      )}
      {isSplitSeed && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-500/30">
          <Scissors className="h-4 w-4 text-white drop-shadow" />
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Assignable face (for unassigned pool)
// ---------------------------------------------------------------------------

function AssignableFace({
  face,
  persons,
  onAssign,
}: {
  face: FaceDetectionCompact;
  persons: PersonCluster[];
  onAssign: (personId: string) => void;
}) {
  const { t } = useTranslation('faces');
  const [showMenu, setShowMenu] = useState(false);

  if (!face.crop_url) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-md ring-1 ring-amber-400 transition-all hover:ring-2 hover:ring-primary-500 dark:ring-amber-600"
      >
        <img
          src={face.crop_url}
          alt="Face"
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute bottom-0 end-0 rounded-tl bg-primary-500/80 p-0.5">
          <Plus className="h-2.5 w-2.5 text-white" />
        </div>
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute start-0 top-full z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <p className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('assignTo')}
            </p>
            {persons.map((person) => (
              <button
                key={person.id}
                onClick={() => {
                  onAssign(person.id);
                  setShowMenu(false);
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-start text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {person.representative_face_url ? (
                  <img
                    src={person.representative_face_url}
                    alt=""
                    className="h-6 w-6 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-600">
                    <User className="h-3 w-3 text-gray-400" />
                  </div>
                )}
                <span className="truncate">
                  {person.name || t('unknownPerson')}
                </span>
                <span className="ms-auto text-xs text-gray-400">
                  {person.face_count}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New cluster appearing with animation
// ---------------------------------------------------------------------------

function NewClusterBanner({
  movedCount,
  visible,
}: {
  movedCount: number;
  visible: boolean;
}) {
  const { t } = useTranslation('faces');

  return (
    <div
      className={`overflow-hidden transition-all duration-700 ease-out ${
        visible ? 'mb-4 max-h-16 opacity-100' : 'mb-0 max-h-0 opacity-0'
      }`}
    >
      <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-3 dark:bg-blue-900/20">
        <Scissors className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
          {t('splitSuccess')} ({movedCount} {t('faceCountShort', { count: movedCount })})
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single cluster review section
// ---------------------------------------------------------------------------

function ClusterSection({
  cluster,
  onRemoveFaces,
  onSplit,
  onDismiss,
  isRemoving,
  isSplitting,
  isDismissing,
  movingFaceIds,
}: {
  cluster: PersonCluster;
  onRemoveFaces: (personId: string, faceIds: string[]) => void;
  onSplit: (personId: string, faceId: string) => void;
  onDismiss: (personId: string) => void;
  isRemoving: boolean;
  isSplitting: boolean;
  isDismissing: boolean;
  movingFaceIds: Set<string>;
}) {
  const { t } = useTranslation('faces');
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedFaces, setSelectedFaces] = useState<Set<string>>(new Set());
  const [splitSeedId, setSplitSeedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'remove' | 'split'>('split');

  const toggleFace = (faceId: string) => {
    setSelectedFaces((prev) => {
      const next = new Set(prev);
      if (next.has(faceId)) next.delete(faceId);
      else next.add(faceId);
      return next;
    });
    setSplitSeedId(null);
  };

  const handleSplitSelect = (faceId: string) => {
    setSplitSeedId((prev) => (prev === faceId ? null : faceId));
    setSelectedFaces(new Set());
  };

  const handleRemove = () => {
    if (selectedFaces.size === 0) return;
    onRemoveFaces(cluster.id, Array.from(selectedFaces));
    setSelectedFaces(new Set());
  };

  const handleSplit = () => {
    if (!splitSeedId) return;
    onSplit(cluster.id, splitSeedId);
    setSplitSeedId(null);
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-3 px-4 py-3"
      >
        {cluster.representative_face_url ? (
          <img
            src={cluster.representative_face_url}
            alt={cluster.name || t('unknownPerson')}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <User className="h-5 w-5 text-gray-400" />
          </div>
        )}
        <div className="min-w-0 flex-1 text-start">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {cluster.name || t('unknownPerson')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('faceCountShort', { count: cluster.face_count })}
            {' · '}
            {t('photoCountShort', { count: cluster.photo_count })}
          </p>
        </div>

        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Dismiss cluster button */}
      {isExpanded && (
        <div className="flex justify-end border-t border-gray-100 px-4 py-1.5 dark:border-gray-800">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(t('dismissConfirm'))) {
                onDismiss(cluster.id);
              }
            }}
            disabled={isDismissing}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
          >
            {isDismissing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Ban className="h-3 w-3" />
            )}
            {t('dismissCluster')}
          </button>
        </div>
      )}

      {/* Expanded face grid */}
      {isExpanded && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          {/* Mode toggle */}
          <div className="mb-2 flex items-center gap-2">
            <button
              onClick={() => { setMode('split'); setSelectedFaces(new Set()); }}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === 'split'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`}
            >
              <Scissors className="me-1 inline h-3 w-3" />
              {t('split')}
            </button>
            <button
              onClick={() => { setMode('remove'); setSplitSeedId(null); }}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                mode === 'remove'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`}
            >
              <Trash2 className="me-1 inline h-3 w-3" />
              {t('removeMarked', { count: 0 })}
            </button>
            <span className="text-xs text-gray-400">
              {mode === 'split' ? t('splitHint') : t('tapToMarkWrong')}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {cluster.sample_faces.map((face) => (
              <SelectableFace
                key={face.id}
                face={face}
                isSelected={selectedFaces.has(face.id)}
                isSplitSeed={splitSeedId === face.id}
                isMoving={movingFaceIds.has(face.id)}
                onToggle={() => toggleFace(face.id)}
                onSplitSelect={() => handleSplitSelect(face.id)}
                mode={mode}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="mt-3 flex items-center gap-2">
            {mode === 'split' && splitSeedId && (
              <button
                onClick={handleSplit}
                disabled={isSplitting}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {isSplitting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Scissors className="h-3 w-3" />
                )}
                {t('split')}
              </button>
            )}

            {mode === 'remove' && selectedFaces.size > 0 && (
              <>
                <button
                  onClick={handleRemove}
                  disabled={isRemoving}
                  className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                >
                  {isRemoving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                  {t('removeMarked', { count: selectedFaces.size })}
                </button>
                <button
                  onClick={() => setSelectedFaces(new Set())}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {t('clearSelection')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main cluster review component
// ---------------------------------------------------------------------------

export function ClusterReviewStep({
  eventId,
}: {
  eventId: string;
}) {
  const { t } = useTranslation('faces');
  const { data: clusters, isLoading: clustersLoading } = useWizardClusters(eventId);
  const { data: unassignedData, isLoading: unassignedLoading } = useUnassignedFaces(eventId);
  const removeFacesMutation = useRemoveFaces();
  const assignFacesMutation = useAssignFaces();
  const splitMutation = useSplitPerson();
  const dismissMutation = useDismissCluster();

  const [movingFaceIds, setMovingFaceIds] = useState<Set<string>>(new Set());
  const [splitBanner, setSplitBanner] = useState<{ visible: boolean; count: number }>({
    visible: false,
    count: 0,
  });

  // Clear banner after a delay
  useEffect(() => {
    if (splitBanner.visible) {
      const timer = setTimeout(() => setSplitBanner({ visible: false, count: 0 }), 4000);
      return () => clearTimeout(timer);
    }
  }, [splitBanner.visible]);

  const handleRemoveFaces = (personId: string, faceIds: string[]) => {
    removeFacesMutation.mutate({ personId, faceIds });
  };

  const handleAssignFace = (personId: string, faceId: string) => {
    assignFacesMutation.mutate({ personId, faceIds: [faceId] });
  };

  const handleDismiss = (personId: string) => {
    dismissMutation.mutate(personId);
  };

  const handleSplit = (personId: string, faceId: string) => {
    splitMutation.mutate(
      { personId, faceId },
      {
        onSuccess: (data) => {
          // Animate moved faces
          const ids = new Set(data.moved_face_ids);
          setMovingFaceIds(ids);
          setSplitBanner({ visible: true, count: ids.size });

          // Clear animation after transition completes
          setTimeout(() => setMovingFaceIds(new Set()), 600);
        },
      },
    );
  };

  if (clustersLoading) {
    return (
      <div className="flex flex-col items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const unassignedFaces = unassignedData?.faces ?? [];
  const unassignedTotal = unassignedData?.total ?? 0;

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('reviewFixTitle')}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('reviewFixDescription')}
        </p>
      </div>

      <NewClusterBanner
        movedCount={splitBanner.count}
        visible={splitBanner.visible}
      />

      {/* Cluster sections */}
      <div className="space-y-4">
        {clusters?.map((cluster) => (
          <ClusterSection
            key={cluster.id}
            cluster={cluster}
            onRemoveFaces={handleRemoveFaces}
            onSplit={handleSplit}
            onDismiss={handleDismiss}
            isRemoving={removeFacesMutation.isPending}
            isSplitting={splitMutation.isPending}
            isDismissing={dismissMutation.isPending}
            movingFaceIds={movingFaceIds}
          />
        ))}
      </div>

      {(!clusters || clusters.length === 0) && (
        <p className="py-8 text-center text-sm text-gray-500">
          {t('noClusters')}
        </p>
      )}

      {/* Unassigned faces pool */}
      {unassignedTotal > 0 && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/10">
          <p className="mb-2 text-sm font-medium text-amber-800 dark:text-amber-300">
            {t('unassignedFaces', { count: unassignedTotal })}
          </p>
          <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">
            {t('tapToAssign')}
          </p>

          {unassignedLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {unassignedFaces.map((face: FaceDetectionCompact) => (
                <AssignableFace
                  key={face.id}
                  face={face}
                  persons={clusters ?? []}
                  onAssign={(personId) => handleAssignFace(personId, face.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
